import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MARKET_REFRESH_MS, contestsAPI, playersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Toast from '../components/Toast';
import OrderBucket from '../components/OrderBucket';
import { PlayerPhoto } from '../components/PlayerCard';
import useMarketStatus from '../hooks/useMarketStatus';
import MarketLockBanner from '../components/MarketLockBanner';
import { LBHeader, LBRow, LBCard } from '../components/LeaderboardRow';
import { ArrowLeft, Copy, Check, TrendingUp, TrendingDown, Lock, MinusCircle, Search, ShoppingCart } from 'lucide-react';

export default function ContestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { status: marketStatus } = useMarketStatus();
  const marketLocked = marketStatus?.isLive || !marketStatus?.tradingWindowOpen;

  const [contest, setContest] = useState(null);
  const [myPart, setMyPart] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [bucketData, setBucketData] = useState({ orders: [], summary: null });
  const [tab, setTab] = useState('market');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [copied, setCopied] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const fetchBucket = useCallback(async () => {
    try {
      const bucketRes = await contestsAPI.bucket(id);
      setBucketData(bucketRes.data || { orders: [], summary: null });
    } catch (err) {
      if (err.response?.status === 404) {
        setBucketData({ orders: [], summary: null });
        return;
      }
      console.error(err);
    }
  }, [id]);

  const fetchAll = useCallback(async () => {
    try {
      const [detailRes, portRes, lbRes, playersRes, bucketRes] = await Promise.all([
        contestsAPI.detail(id),
        contestsAPI.portfolio(id),
        contestsAPI.leaderboard(id),
        playersAPI.getAll(),
        contestsAPI.bucket(id).catch(err => {
          if (err.response?.status === 404) {
            return { data: { orders: [], summary: null } };
          }
          throw err;
        }),
      ]);

      setContest(detailRes.data.contest);
      setMyPart(detailRes.data.myParticipation);
      setPortfolio(portRes.data);
      setLeaderboard(lbRes.data);
      setAllPlayers(playersRes.data);
      setBucketData(bucketRes.data || { orders: [], summary: null });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchAll();
    const interval = setInterval(fetchAll, MARKET_REFRESH_MS);
    return () => clearInterval(interval);
  }, [fetchAll]);

  const holdingMap = React.useMemo(() => {
    const map = {};
    portfolio.forEach(holding => {
      map[holding.player_id] = holding;
    });
    return map;
  }, [portfolio]);

  const bucketMap = React.useMemo(() => {
    const map = {};
    (bucketData?.orders || []).forEach(order => {
      map[`${order.player_id}:${order.type}`] = order;
    });
    return map;
  }, [bucketData]);

  const handleAddToBucket = async (player_id, type) => {
    if (marketLocked) {
      setToast({
        msg: marketStatus?.isLive
          ? 'Trading is paused during a live IPL match'
          : (marketStatus?.tradingWindowMessage || 'Trading is currently locked'),
        type: 'error'
      });
      return;
    }

    try {
      const response = await contestsAPI.addToBucket(id, player_id, type);
      setToast({ msg: response.data.message, type: 'success' });
      fetchBucket();
    } catch (err) {
      setToast({ msg: err.response?.data?.error || 'Failed to add order', type: 'error' });
    }
  };

  const handleRemoveFromBucket = async (orderId) => {
    try {
      const response = await contestsAPI.removeFromBucket(id, orderId);
      setToast({ msg: response.data.message, type: 'info' });
      fetchBucket();
    } catch (err) {
      setToast({ msg: err.response?.data?.error || 'Failed to remove order', type: 'error' });
    }
  };

  const handleBucketExecuted = async (result) => {
    setMyPart(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        points: result.newBalance ?? prev.points,
        trade_count: result.trade_count ?? prev.trade_count,
      };
    });
    await fetchAll();
  };

  const copyCode = () => {
    if (contest?.invite_code) {
      navigator.clipboard.writeText(contest.invite_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const filteredPlayers = React.useMemo(() => {
    if (!search) return allPlayers;
    const query = search.toLowerCase();
    return allPlayers.filter(
      player => player.name.toLowerCase().includes(query) || player.team_short.toLowerCase().includes(query)
    );
  }, [allPlayers, search]);

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!contest) return <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Contest not found</div>;
  if (!myPart) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ color: 'var(--text-muted)', marginBottom: 12 }}>You haven't joined this contest.</p>
        <button className="btn btn-buy" onClick={() => navigate('/contests')}>Browse Contests</button>
      </div>
    );
  }

  const tradesLeft = contest.max_trades - myPart.trade_count;
  const portfolioValue = portfolio.reduce((sum, holding) => sum + parseFloat(holding.current_stock_price), 0);
  const totalValue = parseFloat(myPart.points) + portfolioValue;

  return (
    <div>
      {toast && <Toast message={toast.msg} type={toast.type} onDismiss={() => setToast(null)} />}

      <button
        onClick={() => navigate('/contests')}
        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.85rem', marginBottom: '1rem' }}
      >
        <ArrowLeft size={16} /> Contests
      </button>

      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', fontWeight: 700 }}>{contest.name}</h2>
            {contest.description && <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: 3 }}>{contest.description}</p>}
            <div style={{ display: 'flex', gap: 10, marginTop: 8, flexWrap: 'wrap' }}>
              <span className="stat-chip">{contest.participant_count} players</span>
              <span className="stat-chip">{contest.start_points} start pts</span>
              <span className="stat-chip">{contest.max_trades} trades</span>
            </div>
          </div>
          <button
            onClick={copyCode}
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: '6px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: '0.8rem',
              color: 'var(--text-secondary)',
              fontFamily: 'var(--font-mono)',
              letterSpacing: 2,
            }}
          >
            {copied ? <Check size={13} color="var(--green)" /> : <Copy size={13} />}
            {contest.invite_code}
          </button>
        </div>
      </div>

      <MarketLockBanner status={marketStatus} tradesRemaining={tradesLeft} />

      <div className="metrics-row" style={{ marginBottom: '1.25rem' }}>
        <div className="metric-card">
          <div className="metric-label">Total Value</div>
          <div className="metric-value accent">{totalValue.toFixed(1)} pts</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Cash</div>
          <div className="metric-value">{parseFloat(myPart.points).toFixed(1)}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Invested</div>
          <div className="metric-value">{portfolioValue.toFixed(1)}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Trades left</div>
          <div className={`metric-value ${tradesLeft <= 5 ? 'red' : tradesLeft <= 15 ? 'accent' : ''}`}>{tradesLeft}</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: '1.25rem', flexWrap: 'wrap' }}>
        {[['market', 'Market'], ['holdings', 'My Holdings'], ['leaderboard', 'Leaderboard']].map(([key, label]) => (
          <button key={key} className={`filter-chip${tab === key ? ' active' : ''}`} onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'market' && (
        <>
          <div className="search-wrap" style={{ marginBottom: '1rem' }}>
            <Search size={14} className="search-icon" />
            <input
              className="search-input"
              style={{ maxWidth: '100%', width: '100%' }}
              placeholder="Search player..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="players-grid">
            {filteredPlayers.map(player => {
              const holding = holdingMap[player.id];
              const buyOrder = bucketMap[`${player.id}:buy`];
              const sellOrder = bucketMap[`${player.id}:sell`];
              const teamColor = player.team_color || '#3b82f6';
              const changePct = parseFloat(player.price_change_pct || 0);

              return (
                <div key={player.id} className="player-card">
                  <div className="player-card-header">
                    <PlayerPhoto player={player} size={42} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {player.name}
                        {player.is_playing_today && <span className="live-badge"><span className="live-dot" /> LIVE</span>}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                        <span style={{ color: teamColor, fontWeight: 600 }}>{player.team_short}</span>
                      </div>
                    </div>
                  </div>
                  <div className="price-section">
                    <div>
                      <div style={{ fontSize: '0.67rem', color: 'var(--text-muted)' }}>PRICE</div>
                      <div className="price-current">
                        {parseFloat(player.current_stock_price).toFixed(1)} <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>pts</span>
                      </div>
                    </div>
                    <span className={`price-change ${changePct > 0 ? 'up' : changePct < 0 ? 'down' : 'flat'}`}>
                      {changePct >= 0 ? '+' : ''}{changePct.toFixed(2)}%
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {holding ? (
                      <>
                        <div style={{ flex: 1, fontSize: '0.74rem', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' }}>
                          Bought @ {parseFloat(holding.buy_price).toFixed(1)}
                        </div>
                        {sellOrder ? (
                          <button
                            className="btn btn-sell"
                            style={{
                              flex: 1,
                              padding: '8px',
                              borderRadius: 8,
                              border: '1px solid rgba(239,68,68,0.3)',
                              background: 'rgba(239,68,68,0.08)',
                              color: 'var(--red)',
                              cursor: 'pointer',
                              fontSize: '0.8rem',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 5,
                            }}
                            onClick={() => handleRemoveFromBucket(sellOrder.id)}
                          >
                            <MinusCircle size={12} /> In Bucket - Remove
                          </button>
                        ) : (
                          <button
                            className="btn btn-sell"
                            style={{ flex: 1, opacity: marketLocked ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}
                            onClick={() => handleAddToBucket(player.id, 'sell')}
                            disabled={marketLocked}
                          >
                            {marketLocked && <Lock size={11} />}
                            {marketLocked ? 'Locked' : 'Sell'}
                          </button>
                        )}
                      </>
                    ) : buyOrder ? (
                      <button
                        className="btn btn-buy"
                        style={{
                          width: '100%',
                          padding: '8px',
                          borderRadius: 8,
                          border: '1px solid rgba(16,185,129,0.3)',
                          background: 'rgba(16,185,129,0.08)',
                          color: 'var(--green)',
                          cursor: 'pointer',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 5,
                        }}
                        onClick={() => handleRemoveFromBucket(buyOrder.id)}
                      >
                        <MinusCircle size={12} /> In Bucket - Remove
                      </button>
                    ) : (
                      <button
                        className="btn btn-buy"
                        style={{ width: '100%', opacity: marketLocked ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}
                        onClick={() => handleAddToBucket(player.id, 'buy')}
                        disabled={marketLocked}
                      >
                        {marketLocked && <Lock size={11} />}
                        {marketLocked ? 'Locked' : `Buy · ${parseFloat(player.current_stock_price).toFixed(1)}`}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {tab === 'holdings' && (
        portfolio.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
            No holdings yet. Buy players from the Market tab.
          </div>
        ) : (
          portfolio.map(holding => {
            const pnl = parseFloat(holding.pnl || 0);
            const teamColor = holding.team_color || '#3b82f6';
            const sellOrder = bucketMap[`${holding.player_id}:sell`];

            return (
              <div key={holding.id} className="portfolio-item">
                <PlayerPhoto player={holding} size={40} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>{holding.name}</div>
                  <div style={{ fontSize: '0.74rem', color: 'var(--text-secondary)', marginTop: 2 }}>
                    <span style={{ color: teamColor, fontWeight: 600 }}>{holding.team_short}</span> · Bought @ {parseFloat(holding.buy_price).toFixed(1)}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.1rem' }}>{parseFloat(holding.current_stock_price).toFixed(1)}</div>
                  <div style={{ fontSize: '0.74rem', color: pnl >= 0 ? 'var(--green)' : 'var(--red)', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3 }}>
                    {pnl >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                    {pnl >= 0 ? '+' : ''}{pnl.toFixed(1)} ({parseFloat(holding.pnl_pct || 0).toFixed(1)}%)
                  </div>
                </div>
                {sellOrder ? (
                  <button
                    className="btn btn-sell"
                    style={{
                      padding: '8px 10px',
                      borderRadius: 8,
                      border: '1px solid rgba(239,68,68,0.3)',
                      background: 'rgba(239,68,68,0.08)',
                      color: 'var(--red)',
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                    }}
                    onClick={() => handleRemoveFromBucket(sellOrder.id)}
                  >
                    <MinusCircle size={11} /> In Bucket - Remove
                  </button>
                ) : (
                  <button
                    className="btn btn-sell"
                    style={{ padding: '7px 14px', fontSize: '0.8rem', flexShrink: 0, opacity: marketLocked ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: 4 }}
                    onClick={() => handleAddToBucket(holding.player_id, 'sell')}
                    disabled={marketLocked}
                  >
                    {marketLocked && <Lock size={11} />}
                    {marketLocked ? 'Locked' : 'Sell'}
                  </button>
                )}
              </div>
            );
          })
        )
      )}

      {tab === 'leaderboard' && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {!isMobile && <LBHeader showAdd={false} />}
          {leaderboard.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>No participants yet</div>
          ) : leaderboard.map(entry => {
            const isMe = entry.user_id === user?.id;
            const RowComp = isMobile ? LBCard : LBRow;
            return (
              <RowComp
                key={entry.user_id}
                entry={entry}
                isMe={isMe}
                requestState="friends"
              />
            );
          })}
        </div>
      )}

      <OrderBucket
        bucket={bucketData}
        refreshBucket={fetchBucket}
        onExecuted={handleBucketExecuted}
        onToast={(msg, type) => setToast({ msg, type })}
        apiBase={`/contests/${id}/bucket`}
        title="Contest Bucket"
        subtitle="This bucket is only for this contest"
        emptyText="Your contest bucket is empty"
        emptyHint="Add contest buy or sell orders from the market and holdings tabs"
      />

      {(bucketData?.orders?.length || 0) > 0 && (
        <div
          style={{
            position: 'fixed',
            right: 20,
            bottom: 24,
            zIndex: 149,
            fontSize: '0.72rem',
            color: 'var(--text-muted)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            pointerEvents: 'none',
          }}
        >
          <ShoppingCart size={13} />
          Contest-only orders
        </div>
      )}
    </div>
  );
}
